import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutSingleGen } from '../layoutSingle';
import { OptsSingle } from '../optsSingle';
import { GUtil } from '../../pdf/gutil';
import { genSingle } from '../../core/genHelper';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/cut';
import '../../pdf/draw/helpers';

/**
 * Pocitani_z_hlavy (Mental Math Worksheet)
 *
 * Layout: Two columns with numbered equations. Alternating row backgrounds.
 * Equations are parsed to color-code operands (blue, black op, red, green result).
 *
 * Migrated from C# EduFrame.Single.Generators.Pocitani_z_hlavy
 */
export class ZHlavy implements ILayoutSingleGen {
  public async generate(pdf: PdfGen, opts: OptsSingle): Promise<void> {
    const colSize = new PdfSize(PdfUnit.FromMillimeters(73), PdfUnit.FromMillimeters(250));
    const originX = PdfSize.A4.width / 2 - colSize.width;
    const originY = (PdfSize.A4.height - colSize.height) / 2;
    const linePlus = PdfUnit.FromMillimeters(11);
    const lineHeight = PdfUnit.FromMillimeters(8);

    const font = new PdfFont(PdfFont.FONT_NAME, 14);

    const darkBlue = new PdfBrush(PdfColor.fromHex('#00008B'));
    const darkRed = new PdfBrush(PdfColor.fromHex('#8B0000'));
    const darkGreen = new PdfBrush(PdfColor.fromHex('#006400'));
    const darkGray = new PdfBrush(PdfColor.fromHex('#A9A9A9'));

    const itemsPerColumn = Math.floor((colSize.height - PdfUnit.FromMillimeters(40)) / linePlus);
    const itemsPerPage = itemsPerColumn * 2;
    const totalSheets = 1;

    const gen = genSingle(opts.data, opts.generator, itemsPerPage * totalSheets, false, false);

    for (let sIdx = 0; sIdx < totalSheets; sIdx++) {
      const page = pdf.addPage();
      const currentGen = gen.slice(sIdx * itemsPerPage, (sIdx + 1) * itemsPerPage);

      const rect1 = new PdfRect(originX, originY, colSize.width, colSize.height);
      const rect2 = new PdfRect(originX + colSize.width, originY, colSize.width, colSize.height);

      page.cutCross(new PdfPoint(rect1.left, rect1.top));
      page.cutCross(new PdfPoint(rect1.right, rect1.bottom));
      page.cutCross(new PdfPoint(rect2.right, rect2.top));

      this.drawColumn(
        page,
        rect1,
        currentGen,
        0,
        font,
        linePlus,
        lineHeight,
        darkBlue,
        darkRed,
        darkGreen,
        darkGray
      );
      this.drawColumn(
        page,
        rect2,
        currentGen,
        itemsPerColumn,
        font,
        linePlus,
        lineHeight,
        darkBlue,
        darkRed,
        darkGreen,
        darkGray
      );

      page.drawRectangle(GUtil.basePen, null, rect1);
      page.drawRectangle(GUtil.basePen, null, rect2);
    }
  }

  private drawColumn(
    page: any,
    rect: PdfRect,
    gen: any[],
    startIndex: number,
    font: PdfFont,
    linePlus: number,
    lineHeight: number,
    darkBlue: PdfBrush,
    darkRed: PdfBrush,
    darkGreen: PdfBrush,
    darkGray: PdfBrush
  ): void {
    let pos = rect.y + PdfUnit.FromMillimeters(40);
    let number = 1;
    let itemIndex = startIndex;

    while (pos < rect.bottom - PdfUnit.FromMillimeters(20) && itemIndex < gen.length) {
      const questionText = gen[itemIndex].texts[0];
      const answerText = gen[itemIndex].answer;

      const numberRect = new PdfRect(
        rect.x + PdfUnit.FromMillimeters(3),
        pos,
        PdfUnit.FromMillimeters(7),
        lineHeight
      );
      const aRect = new PdfRect(
        rect.x + PdfUnit.FromMillimeters(10),
        pos,
        PdfUnit.FromMillimeters(14),
        lineHeight
      );
      const opRect = new PdfRect(
        rect.x + PdfUnit.FromMillimeters(25),
        pos,
        PdfUnit.FromMillimeters(7),
        lineHeight
      );
      const bRect = new PdfRect(
        rect.x + PdfUnit.FromMillimeters(33),
        pos,
        PdfUnit.FromMillimeters(14),
        lineHeight
      );
      const eqRect = new PdfRect(
        rect.x + PdfUnit.FromMillimeters(48),
        pos,
        PdfUnit.FromMillimeters(7),
        lineHeight
      );
      const resRect = new PdfRect(
        rect.x + PdfUnit.FromMillimeters(57),
        pos,
        PdfUnit.FromMillimeters(14),
        lineHeight
      );

      if (number % 2 === 1) {
        page.drawBackground(new PdfRect(rect.x, pos, rect.width, linePlus));
      }

      page.drawString(`${number}.`, font, darkGray, null, numberRect, ETextAlign.CenterRight);

      const match = questionText.match(/^(\d+(?:\.\d*)?)\s*([^\d]+)\s*(\d+(?:\.\d*)?)\s*(=)?$/);

      if (match) {
        page.drawString(match[1], font, darkBlue, null, aRect, ETextAlign.CenterRight);
        page.drawString(match[2].trim(), font, PdfBrush.Black, null, opRect, ETextAlign.Center);
        page.drawString(match[3], font, darkRed, null, bRect, ETextAlign.CenterRight);
        page.drawString(match[4] || '=', font, PdfBrush.Black, null, eqRect, ETextAlign.Center);
        page.drawFillStringCenter(answerText, font, darkGreen, resRect);
      } else {
        const qRect = new PdfRect(aRect.x, pos, eqRect.right - aRect.x, lineHeight);
        page.drawFillStringCenter(questionText, font, PdfBrush.Black, qRect);
        page.drawFillStringCenter(answerText, font, darkGreen, resRect);
      }

      pos += linePlus;
      number++;
      itemIndex++;
    }
  }
}
